import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    TrendingUp, TrendingDown, Users, DollarSign, ShoppingCart,
    Clock, Award, Trophy, Medal, Star, Target, BarChart3,
    Calendar, ChevronRight, ArrowUpRight, ArrowDownRight,
    Package, Percent, Timer, CheckCircle, XCircle, RefreshCw,
    Crown, Flame, Zap
} from 'lucide-react';
import MobilePageHeader from '../components/MobilePageHeader';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================
// SERVER PERFORMANCE PAGE
// ============================================================

const ServerPerformancePage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('week');
    const [data, setData] = useState(null);
    const [selectedServer, setSelectedServer] = useState(null);
    const [serverDetail, setServerDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    
    // Fetch performance data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api().get(`/reports/server-performance?period=${period}`);
            setData(response.data);
        } catch (error) {
            console.error('Error fetching performance data:', error);
            toast.error('Erreur de chargement des données');
        } finally {
            setLoading(false);
        }
    }, [api, period]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Fetch server detail
    const fetchServerDetail = async (serverId) => {
        setDetailLoading(true);
        try {
            const response = await api().get(`/reports/server-performance/${serverId}?period=${period}`);
            setServerDetail(response.data);
        } catch (error) {
            console.error('Error fetching server detail:', error);
            toast.error('Erreur de chargement');
        } finally {
            setDetailLoading(false);
        }
    };
    
    const handleServerClick = (server) => {
        setSelectedServer(server);
        fetchServerDetail(server.server_id);
    };
    
    // Format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR').format(value || 0) + ' F';
    };
    
    // Get rank icon
    const getRankIcon = (rank) => {
        if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
        if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
        if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
        return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-muted-foreground">#{rank}</span>;
    };
    
    // Get performance color based on metric
    const getPerformanceColor = (value, metric) => {
        if (metric === 'punctuality') {
            if (value >= 90) return 'text-green-600';
            if (value >= 70) return 'text-yellow-600';
            return 'text-red-600';
        }
        return 'text-primary';
    };
    
    // Period labels
    const periodLabels = {
        today: "Aujourd'hui",
        yesterday: "Hier",
        week: "Cette semaine",
        month: "Ce mois",
        last_month: "Mois dernier"
    };
    
    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    return (
        <div className="space-y-4 md:space-y-6" data-testid="server-performance-page">
            {/* Mobile Header */}
            <MobilePageHeader 
                title="Performance Serveurs"
                subtitle={periodLabels[period]}
                icon={BarChart3}
                backPath="/dashboard"
            />
            
            {/* Desktop Header */}
            <div className="hidden md:flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Rapport de Performance</h1>
                    <p className="text-muted-foreground">Analyse des performances par serveur</p>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="today">Aujourd'hui</SelectItem>
                            <SelectItem value="yesterday">Hier</SelectItem>
                            <SelectItem value="week">Cette semaine</SelectItem>
                            <SelectItem value="month">Ce mois</SelectItem>
                            <SelectItem value="last_month">Mois dernier</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
                        <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
                    </Button>
                </div>
            </div>
            
            {/* Mobile Period Selector */}
            <div className="md:hidden flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
                {Object.entries(periodLabels).map(([key, label]) => (
                    <Button
                        key={key}
                        variant={period === key ? "default" : "outline"}
                        size="sm"
                        className="shrink-0"
                        onClick={() => setPeriod(key)}
                    >
                        {label}
                    </Button>
                ))}
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-muted-foreground">Commandes</p>
                                <p className="text-2xl font-bold">{data?.totals?.total_orders || 0}</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <ShoppingCart className="w-5 h-5 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-muted-foreground">Ventes</p>
                                <p className="text-xl md:text-2xl font-bold">{formatCurrency(data?.totals?.total_sales)}</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-green-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-muted-foreground">Serveurs</p>
                                <p className="text-2xl font-bold">{data?.totals?.total_servers || 0}</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <Users className="w-5 h-5 text-purple-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-muted-foreground">Ticket moyen</p>
                                <p className="text-xl md:text-2xl font-bold">{formatCurrency(data?.totals?.avg_ticket_global)}</p>
                            </div>
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                <Target className="w-5 h-5 text-orange-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Top 3 Podium - Mobile */}
            {data?.servers?.length >= 3 && (
                <Card className="md:hidden">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            Top Performers
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pb-4">
                        <div className="flex items-end justify-center gap-2">
                            {/* 2nd place */}
                            <div className="flex flex-col items-center">
                                <Avatar className="w-12 h-12 border-2 border-gray-300">
                                    <AvatarFallback className="bg-gray-100 text-gray-600 font-bold">
                                        {data.servers[1]?.server_name?.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="mt-1 px-2 py-0.5 bg-gray-100 rounded text-xs font-medium">2</div>
                                <p className="text-xs mt-1 text-center truncate w-20">{data.servers[1]?.server_name?.split(' ')[0]}</p>
                                <p className="text-xs text-muted-foreground">{formatCurrency(data.servers[1]?.total_sales)}</p>
                            </div>
                            
                            {/* 1st place */}
                            <div className="flex flex-col items-center -mt-4">
                                <Crown className="w-6 h-6 text-yellow-500 mb-1" />
                                <Avatar className="w-16 h-16 border-3 border-yellow-400">
                                    <AvatarFallback className="bg-yellow-50 text-yellow-700 font-bold text-lg">
                                        {data.servers[0]?.server_name?.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="mt-1 px-2 py-0.5 bg-yellow-100 rounded text-xs font-bold text-yellow-700">1</div>
                                <p className="text-sm font-medium mt-1 text-center truncate w-24">{data.servers[0]?.server_name?.split(' ')[0]}</p>
                                <p className="text-xs text-muted-foreground font-medium">{formatCurrency(data.servers[0]?.total_sales)}</p>
                            </div>
                            
                            {/* 3rd place */}
                            <div className="flex flex-col items-center">
                                <Avatar className="w-12 h-12 border-2 border-amber-400">
                                    <AvatarFallback className="bg-amber-50 text-amber-700 font-bold">
                                        {data.servers[2]?.server_name?.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="mt-1 px-2 py-0.5 bg-amber-100 rounded text-xs font-medium text-amber-700">3</div>
                                <p className="text-xs mt-1 text-center truncate w-20">{data.servers[2]?.server_name?.split(' ')[0]}</p>
                                <p className="text-xs text-muted-foreground">{formatCurrency(data.servers[2]?.total_sales)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
            
            {/* Server List */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base md:text-lg">Classement par Ventes</CardTitle>
                    <CardDescription className="text-xs md:text-sm">
                        {periodLabels[period]} • Triés par chiffre d'affaires
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    <ScrollArea className="max-h-[500px]">
                        <div className="divide-y">
                            {data?.servers?.map((server, index) => (
                                <button
                                    key={server.server_id}
                                    className="w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
                                    onClick={() => handleServerClick(server)}
                                    data-testid={`server-row-${server.server_id}`}
                                >
                                    {/* Rank */}
                                    <div className="w-8 flex justify-center">
                                        {getRankIcon(server.rank_by_sales)}
                                    </div>
                                    
                                    {/* Avatar */}
                                    <Avatar className="w-10 h-10 shrink-0">
                                        <AvatarFallback className={cn(
                                            "font-semibold",
                                            index === 0 ? "bg-yellow-100 text-yellow-700" :
                                            index === 1 ? "bg-gray-100 text-gray-600" :
                                            index === 2 ? "bg-amber-100 text-amber-700" :
                                            "bg-primary/10 text-primary"
                                        )}>
                                            {server.server_name?.charAt(0) || '?'}
                                        </AvatarFallback>
                                    </Avatar>
                                    
                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium truncate">{server.server_name}</span>
                                            <Badge variant="outline" className="text-[10px] shrink-0">
                                                {server.role}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                            <span>{server.total_orders} cmd</span>
                                            <span>{server.items_sold} articles</span>
                                        </div>
                                    </div>
                                    
                                    {/* Sales */}
                                    <div className="text-right shrink-0">
                                        <p className="font-bold text-green-600">{formatCurrency(server.total_sales)}</p>
                                        <p className="text-xs text-muted-foreground">
                                            Moy: {formatCurrency(server.average_ticket)}
                                        </p>
                                    </div>
                                    
                                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                                </button>
                            ))}
                            
                            {(!data?.servers || data.servers.length === 0) && (
                                <div className="p-8 text-center text-muted-foreground">
                                    <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                                    <p>Aucune donnée pour cette période</p>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </CardContent>
            </Card>
            
            {/* Server Detail Dialog */}
            <Dialog open={!!selectedServer} onOpenChange={(open) => !open && setSelectedServer(null)}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            <Avatar className="w-12 h-12">
                                <AvatarFallback className="bg-primary/10 text-primary font-bold">
                                    {selectedServer?.server_name?.charAt(0)}
                                </AvatarFallback>
                            </Avatar>
                            <div>
                                <p className="font-bold">{selectedServer?.server_name}</p>
                                <p className="text-sm text-muted-foreground font-normal">{selectedServer?.role}</p>
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                    
                    {detailLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : selectedServer && (
                        <div className="space-y-4 mt-4">
                            {/* Key Metrics */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                                    <p className="text-xs text-green-600 dark:text-green-400">Ventes totales</p>
                                    <p className="text-xl font-bold text-green-700 dark:text-green-300">
                                        {formatCurrency(selectedServer.total_sales)}
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                                    <p className="text-xs text-blue-600 dark:text-blue-400">Commandes</p>
                                    <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                                        {selectedServer.total_orders}
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                                    <p className="text-xs text-purple-600 dark:text-purple-400">Ticket moyen</p>
                                    <p className="text-xl font-bold text-purple-700 dark:text-purple-300">
                                        {formatCurrency(selectedServer.average_ticket)}
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                                    <p className="text-xs text-orange-600 dark:text-orange-400">Articles vendus</p>
                                    <p className="text-xl font-bold text-orange-700 dark:text-orange-300">
                                        {selectedServer.items_sold}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Rankings */}
                            <div className="flex items-center justify-center gap-6 py-3 border-y">
                                <div className="text-center">
                                    <p className="text-xs text-muted-foreground">Rang Ventes</p>
                                    <div className="flex items-center justify-center gap-1 mt-1">
                                        {getRankIcon(selectedServer.rank_by_sales)}
                                        <span className="font-bold">#{selectedServer.rank_by_sales}</span>
                                    </div>
                                </div>
                                <div className="w-px h-8 bg-border"></div>
                                <div className="text-center">
                                    <p className="text-xs text-muted-foreground">Rang Commandes</p>
                                    <div className="flex items-center justify-center gap-1 mt-1">
                                        {getRankIcon(selectedServer.rank_by_orders)}
                                        <span className="font-bold">#{selectedServer.rank_by_orders}</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Additional Metrics */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Meilleur ticket</span>
                                    <span className="font-medium">{formatCurrency(selectedServer.highest_ticket)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Temps service moyen</span>
                                    <span className="font-medium">{selectedServer.avg_service_time} min</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Commandes annulées</span>
                                    <span className={cn("font-medium", selectedServer.cancelled_orders > 0 && "text-red-500")}>
                                        {selectedServer.cancelled_orders}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Top Products */}
                            {selectedServer.top_products?.length > 0 && (
                                <div>
                                    <h4 className="font-medium mb-2 flex items-center gap-2">
                                        <Flame className="w-4 h-4 text-orange-500" />
                                        Top Produits Vendus
                                    </h4>
                                    <div className="space-y-2">
                                        {selectedServer.top_products.map((product, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="text-sm truncate max-w-[150px]">{product.name}</span>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-medium">{formatCurrency(product.revenue)}</p>
                                                    <p className="text-xs text-muted-foreground">{product.quantity}x</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ServerPerformancePage;
